/* eslint-disable @reatom/unit-naming-rule */
import { action, atom, reaction, sleep } from '@reatom/framework'

import { AsyncStateN, pipe, Result } from '@blade/std-lib'
import { isLoggedInAtom } from '@root/entities/auth/atoms'
import { reatomResource, SET_NOT_INITIALIZED } from '@root/shared/lib'

import { toDollars } from '../model'

import {
  fetchBasicBalanceRequestAction,
  fetchBasicDepositAddressesRequestAction,
  fetchBasicModeRequestAction,
  fetchBasicPositionsRequestAction,
  setBasicModeRequestAction,
} from './basic-requests'

import type { TradingMode } from '../model'

/**
 * What Basic knows about the person signed in: how they trade, what they can
 * spend, and what that money is holding.
 *
 * None of it is asked for until somebody is signed in. These sit behind the
 * whole app — a dialog mounted next to the router reads the mode — and an
 * answer of 401 is read as a session that has expired, which would take a
 * session apart the moment it was made.
 *
 * A balance and a valuation are not the same kind of number and are not
 * fetched together. The balance is a chain balance less what is already
 * committed, and it changes the moment an order is accepted. The positions
 * are valued at prices a job fetched, so they carry the date they are from
 * and a screen showing them has to say so.
 */

/** Often enough that a deposit shows up while somebody is still looking. */
const BALANCE_POLL_MS = 15_000

export const basicModeStateAtom = reatomResource(
  (ctx) =>
    ctx.spy(isLoggedInAtom)
      ? ctx.schedule(() => fetchBasicModeRequestAction(ctx))
      : SET_NOT_INITIALIZED,
  { name: 'basicModeStateAtom', retry: { enabled: true } },
)

export const basicModeAtom = atom(
  (ctx) =>
    pipe(
      ctx.spy(basicModeStateAtom),
      AsyncStateN.map((it) => it.tradingMode),
      // Before the answer arrives there is no mode to show. Guessing
      // 'advanced' would flash the wrong panel at somebody in Basic.
      AsyncStateN.getOrElseW(() => undefined),
    ),
  'basicModeAtom',
)

export const setBasicModeAction = action(async (ctx, mode: TradingMode) => {
  const result = await ctx.schedule(() => setBasicModeRequestAction(ctx, mode))

  if (Result.isSuccess(result)) {
    await ctx.schedule(() => basicModeStateAtom.refresh(ctx))
  }

  return result
}, 'setBasicModeAction')

export const basicBalanceStateAtom = reatomResource(
  (ctx) =>
    ctx.spy(isLoggedInAtom)
      ? ctx.schedule(() => fetchBasicBalanceRequestAction(ctx))
      : SET_NOT_INITIALIZED,
  { name: 'basicBalanceStateAtom', retry: { enabled: true } },
)

/** The spendable figure in dollars, which is the one every screen shows. */
export const basicAvailableUsdAtom = basicBalanceStateAtom.map((ctx, it) =>
  toDollars(it.available, it.decimals),
)

export const basicPositionsStateAtom = reatomResource(
  (ctx) =>
    ctx.spy(isLoggedInAtom)
      ? ctx.schedule(() => fetchBasicPositionsRequestAction(ctx))
      : SET_NOT_INITIALIZED,
  { name: 'basicPositionsStateAtom', retry: { enabled: true } },
)

export const basicDepositAddressesStateAtom = reatomResource(
  (ctx) =>
    ctx.spy(isLoggedInAtom)
      ? ctx.schedule(() => fetchBasicDepositAddressesRequestAction(ctx))
      : SET_NOT_INITIALIZED,
  {
    name: 'basicDepositAddressesStateAtom',
    // Generated on first ask and never regenerated, so there is nothing to
    // poll and nothing to refresh: the pair is the person's for good.
    loadOnConnect: false,
    retry: { enabled: true },
  },
)

export const loadBasicDepositAddressesAction =
  basicDepositAddressesStateAtom.run

/**
 * Both figures again, after something that changes them.
 *
 * Called after a buy or a sell is accepted rather than after it settles: the
 * money is committed at acceptance, and a balance that still shows it as
 * spendable invites a second order the first one has already spent.
 */
export const refreshBasicAction = action(async (ctx) => {
  await ctx.schedule(() =>
    Promise.all([
      basicBalanceStateAtom.refresh(ctx),
      basicPositionsStateAtom.refresh(ctx),
    ]),
  )
}, 'refreshBasicAction')

export const initializeBasicPollingReaction = reaction(async (ctx) => {
  // Only while the person is in Basic. Polling a balance for somebody
  // trading from their own wallets is a request a minute for a number no
  // screen is showing.
  if (!ctx.spy(isLoggedInAtom) || ctx.spy(basicModeAtom) !== 'basic') {
    return
  }

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  while (true) {
    await ctx.schedule(() => sleep(BALANCE_POLL_MS))
    await ctx.schedule(() => basicBalanceStateAtom.refresh(ctx))
  }
}, 'initializeBasicPollingReaction')
